<template>
  <div class="image-sort">
    <draggable
        v-if="sortedImages.length"
        :class="{ 'sort__draggable--dragging': isDragging }"
        class="sort__draggable columns is-multiline"
        v-model="sortedImages"
        v-bind="draggableOptions"
        @start="onDragStart"
        @end="onDragEnd"
    >
      <div
          class="
          draggable-item
          column
          is-6-tablet is-3-desktop is-2-widescreen is-1-fullhd
        "
          v-for="(image, idx) in sortedImages"
          :key="idx"
      >
        <div class="draggable-inner">
          <img
              v-if="isCampgroundImage"
              class="sort__image"
              :src="isSingleImage ? image : image.url"
              width="400px"
              height="250px"
          />
          <img
              v-else
              class="sort__image"
              :src="image.variants['1_1'].url"
              :width="image.variants['1_1'].width"
              :height="image.variants['1_1'].height"
              :alt="image.title"
          />
          <label>{{ idx + 1 }}</label>
          <div class="controls">
            <button
                v-if="!isCampgroundImage"
                class="button edit-button"
                @click="openModal('edit', image)"
            >
              <span class="fa fa-lg fa-pencil"></span>
            </button>
            <button
                class="button delete-button"
                @click="openModal('delete', image)"
            >
              <span class="fa fa-lg fa-trash"></span>
            </button>
          </div>
        </div>
      </div>
    </draggable>

    <div v-else>
      <article class="message">
        <div class="message-body">
          {{ $t('images.sort.noPictures') }}
        </div>
      </article>
    </div>

    <div
        v-if="!isCampgroundImage"
        class="modal modal-edit isActive"
        :class="{ 'is-active': modals.edit.isActive }"
    >
      <div class="modal-background" @click="closeModal('edit')"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modals.edit.title }}</p>
          <button class="delete" @click="closeModal('edit')"></button>
        </header>
        <section class="modal-card-body" v-if="modals.edit.image">
          <img
              :src="modals.edit.image.variants['4_3_big'].url"
              :width="modals.edit.image.variants['4_3_big'].width"
              :height="modals.edit.image.variants['4_3_big'].height"
              :alt="modals.edit.image.variants['4_3_big'].title"
          />
        </section>
        <section class="input-section" v-if="modals.edit.image">
          <label class="label">{{ $t('images.modals.edit.label') }}</label>
          <input
              type="text"
              class="input"
              v-model="modals.edit.image.title"
              maxlength="256"
          />
        </section>
        <footer class="modal-card-foot is-clearfix">
          <a class="button" @click="closeModal('edit')">{{
              $t('generic.cancel')
            }}</a>
          <a class="button is-primary button-right" @click="saveImage()">
            <span>{{ $t('generic.save') }}</span>
          </a>
        </footer>
      </div>
    </div>
    <div
        class="modal modal-delete isActive"
        :class="{ 'is-active': modals.delete.isActive }"
    >
      <div class="modal-background" @click="closeModal('delete')"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modals.delete.title }}</p>
          <button class="delete" @click="closeModal('delete')"></button>
        </header>
        <section class="modal-card-body" v-if="modals.delete.image">
          <img
              v-if="isCampgroundImage"
              class="sort__image"
              :src="isSingleImage ? modals.delete.image : modals.delete.image.url"
              width="800px"
              height="650px"
          />
          <img
              v-else
              :src="modals.delete.image.variants['4_3_big'].url"
              :width="modals.delete.image.variants['4_3_big'].width"
              :height="modals.delete.image.variants['4_3_big'].height"
              :alt="modals.delete.image.variants['4_3_big'].title"
          />
        </section>
        <footer class="modal-card-foot is-clearfix">
          <a class="button" @click="closeModal('delete')">{{
              $t('generic.cancel')
            }}</a>
          <a class="button is-danger button-right" @click="deleteImage()">
            <span class="icon">
              <i class="fa fa-trash" aria-hidden="true"></i>
            </span>
            <span>{{ $t('images.modals.delete.cta') }}</span>
          </a>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import draggable from 'vuedraggable';

import { Api } from '../../../api';
import { EventBus } from '../../../utils';

export default {
    'name': 'image-sort',
    props: {
        value: {
            type: Array,
            'default': () => {
                return [];
            },
        },
        isCampgroundImage: {
            type: Boolean,
            default: false,
        },
        isSingleImage: {
            type: Boolean,
            default: false,
        },
        imageType: {
            type: String,
            default: '',
        },
    },
    components: {
        draggable,
    },
    data() {
        return {
            isDragging: false,
            sortedImages: [],
            draggableOptions: {
                animation: 150,
                easing: 'cubic-bezier(1, 0, 0, 1)',
                chosenClass: 'draggable-item--chosen',
                dragClass: 'draggable-item--drag',
                ghostClass: 'draggable-item--ghost',
            },
            modals: {
                edit: {
                    isActive: false,
                    title: this.$t('images.modals.edit.title'),
                    content: null,
                },
                delete: {
                    isActive: false,
                    title: this.$t('images.modals.delete.title'),
                    content: null,
                },
            },
        };
    },
    watch: {
        value: {
            handler: 'onValueChanged',
            immediate: true,
        },
    },
    methods: {
        onValueChanged(val) {
            this.sortedImages = val;
        },

        onDragStart() {
            this.isDragging = true;
        },

        onDragEnd({ oldIndex, newIndex }) {
            this.isDragging = false;

            if(newIndex === oldIndex) return;

            const sortData = {
                me: this.sortedImages[newIndex].id,
            };

            if(this.sortedImages[newIndex - 1]) {
                sortData.insert_after = this.sortedImages[newIndex - 1].id;
            }

            Api.campsiteImages.sortImages(sortData).then((response) => {
                if(response.ok) {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`,
                        direction: 'Up',
                        body: this.$t('generic.saveSuccess'),
                    });
                } else {
                    this.onImageEditError();
                }
            }).catch(() => {
                this.onImageEditError();
            });
        },

        openModal(which, image) {
            this.modals[which].content = '';
            this.modals[which].isActive = true;
            this.modals[which].image = image;
        },

        closeModal(which) {
            this.modals[which].isActive = false;
        },

        saveImage() {
            Api.campsiteImages.updateImage(this.modals.edit.image.id, this.modals.edit.image.title).then(response => {
                EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                    title: `<h3>${this.$t('generic.saveSuccessHeadline')}</h3>`,
                    direction: 'Up',
                    body: this.$t('generic.saveSuccess'),
                });
            });
            this.closeModal('edit');
        },

        deleteImage() {
            const imageId = this.modals.delete.image.id;
            const campsiteId = this.$route.params.campsiteId;

            if(this.isCampgroundImage) {
                const imageId = this.isSingleImage ? this.imageType : this.modals.delete.image.id;

                Api.campgroundImages.deleteImages(imageId).then(response => {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('images.notifications.delete.title')}</h3>`,
                        direction: 'Up',
                        body: this.$t('images.notifications.delete.body'),
                        type: 'warning',
                    });
                });
            } else {
                Api.campsiteImages.deleteImage(imageId, campsiteId).then(response => {
                    EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                        title: `<h3>${this.$t('images.notifications.delete.title')}</h3>`,
                        direction: 'Up',
                        body: this.$t('images.notifications.delete.body'),
                        type: 'warning',
                    });
                });
            }
            this.closeModal('delete');
            this.$emit('input', this.value.filter(img => {
                return img.id !== imageId;
            }));
            this.modals.delete.image = null;
        },

        onImageEditError() {
            EventBus.$emit(EventBus.SHOW_NOTIFICATION, {
                title: `<h3>${this.$t('generic.formErrorHeadline')}</h3>`,
                direction: 'Up',
                type: 'danger',
                body: this.$t('generic.formError'),
            });
            Api.campsiteImages.fetchImages(this.$route.params.campsiteId).then((response) => {
                this.sortedImages = response.data;
            });
        },
    },
};
</script>

<style lang="scss">
@import "image-sort.scss";
</style>



// WEBPACK FOOTER //
// src/views/components/campsite-images/ImageSort.vue